import * as d3 from 'd3';

import arcs, {BenchlingArc} from 'molbio/maps/circular/Arcs';
import {GenericArcDatum} from 'molbio/maps/circular/CircularMapTypedefs';
import {getArcForStrand} from 'molbio/maps/circular/CircularUtil';
import * as sequtil from 'molbio/SeqUtil';
import ORF from 'molbio/translations/backbone/ORF';
import {Frame} from 'molbio/translations/backbone/ORFs';
import {getAngles} from 'util/geometry/GeometryUtil';

// When drawing ORFs, we want the start position indicator to be the width of a codon. If a codon would be
// less than DISPLAY_START_POSITION_FLAG_THRESHOLD pixels wide when rendered at the current zoom, we display
// a different start position indicator.
export const DISPLAY_START_POSITION_FLAG_THRESHOLD = 3;

/**
 * A function to filter the list of all ORFs on the sequence to those ORFs that should be rendered based on
 * user settings
 * @param frame What kinds of ORFs should be rendered, e.g. forward, reverse, or a particular reading frame
 * @param minLength The minimum length of ORF to render
 * @param orfs A list of all ORFs on the sequence
 * @param sequenceLength How long the sequence is
 */
export function sortAndFilterORFs({
  frame,
  minLength,
  orfs,
  sequenceLength,
}: {
  frame?: Frame;
  minLength?: number;
  orfs: Array<ORF>;
  sequenceLength: number;
}): Array<ORF> {
  let filteredORFs = orfs;
  if (minLength != null) {
    filteredORFs = filteredORFs.filter(
      (orf) => sequtil.length(orf.start(), orf.end(), sequenceLength) > minLength
    );
  }
  if (frame != null && frame !== 'All') {
    if (frame === 'Forward' || (typeof frame === 'number' && frame > 0)) {
      filteredORFs = filteredORFs.filter((orf) => orf.strand() === 1);
    } else if (frame === 'Reverse' || frame < 0) {
      filteredORFs = filteredORFs.filter((orf) => orf.strand() === -1);
    }

    if (typeof frame === 'number' && [1, 2, 3, -1, -2, -3].includes(frame)) {
      const adjustedFrame = Math.abs(frame) - 1;
      filteredORFs = filteredORFs.filter((orf) => {
        if (orf.strand() === 1) {
          return orf.start() % 3 === adjustedFrame;
        } else {
          return orf.end() % 3 === adjustedFrame;
        }
      });
    }
  }
  return filteredORFs;
}

/**
 * Get the `d` attribute for the path drawing the curved line of an ORF.
 * @param angles The angles, in radians, where the path should start and end.
 * @param layer Which layer the ORF is on. This is a whole number that grows inwards.
 * @param radiusIn A function mapping layer to the amount of pixels that layer should be away from the center.
 * @param vMargin The amount of space between each layer.
 */
export function getLinePath({
  angles,
  layer,
  radiusIn,
  vMargin,
}: {
  angles: GenericArcDatum;
  layer: number;
  radiusIn: d3.ScaleLinear<number, number>;
  vMargin: number;
}): string {
  const radius = (radiusIn(layer + 1) + radiusIn(layer) - vMargin) / 2;
  const arc = (arcs.linearArc().radius(radius) as BenchlingArc<ORF>)
    .startAngle(angles.startAngle)
    .endAngle(angles.endAngle)
    .reverse(false);
  // @ts-expect-error missing arg
  return arc();
}

/**
 * Get the `d` attribute for the path drawing the 'hidden background' click target for this ORF.
 * @param angles The angles, in radians, where the path should start and end.
 * @param layer Which layer the ORF is on. This is a whole number that grows inwards.
 * @param orf The ORF Backbone model being rendered.
 * @param radiusIn A function mapping layer to the amount of pixels that layer should be away from the center.
 */
export function getHiddenBackgroundPath({
  angles,
  layer,
  orf,
  radiusIn,
}: {
  angles: GenericArcDatum;
  layer: number;
  orf: ORF;
  radiusIn: d3.ScaleLinear<number, number>;
}): string {
  const innerRadius = radiusIn(layer + 1);
  const outerRadius = radiusIn(layer + 0.1);
  const arc = d3
    .arc<unknown, ORF>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .startAngle(angles.startAngle)
    .endAngle(angles.endAngle);
  return arc(orf)!;
}

/**
 * Get the `d` attribute for the path drawing the 'tick' version of an ORF start position. For use when a
 * codon would be drawn so small that the usual 'flag' start position would not be distinguishable from a
 * simple line.
 * @param basePairToAngle A linear function mapping an index in base pairs to an angle in radians on the
 *                        unit circle
 * @param index Which start position is being drawn.
 * @param layer Which layer the ORF is on. This is a whole number that grows inwards.
 * @param orf The ORF Backbone model being rendered.
 * @param radiusIn A function mapping layer to the amount of pixels that layer should be away from the center.
 * @param sequenceLength The length of the sequence being rendered.
 * @param vMargin The amount of space between each layer.
 */
export function getStartPositionTickPath({
  basePairToAngle,
  index,
  layer,
  orf,
  radiusIn,
  sequenceLength,
  vMargin,
}: {
  basePairToAngle: (basePair: number) => number;
  index: number;
  layer: number;
  orf: ORF;
  radiusIn: d3.ScaleLinear<number, number>;
  sequenceLength: number;
  vMargin: number;
}): string {
  let position = orf.startPositions()[index];
  if (orf.strand() === -1) {
    position = (position + 1) % sequenceLength;
  }
  const {startAngle, endAngle} = getAngles(position, position + 0.1, sequenceLength, basePairToAngle);
  const innerRadius = radiusIn(layer + 1);
  const outerRadius = radiusIn(layer) - vMargin;
  const arc = d3
    .arc<unknown, ORF>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .startAngle(startAngle)
    .endAngle(endAngle);
  return arc(orf)!;
}

/**
 * Get the `d` attribute for the path drawing a start position flag for an ORF.
 * @param basePairToAngle A linear function mapping an index in base pairs to an angle in radians on the
 *                        unit circle
 * @param index Which start position is being drawn.
 * @param layer Which layer the ORF is on. This is a whole number that grows inwards.
 * @param orf The ORF Backbone model being rendered.
 * @param radiusIn A function mapping layer to the amount of pixels that layer should be away from the center.
 * @param sequenceLength The length of the sequence being rendered.
 * @param vMargin The amount of space between each layer.
 */
export function getStartPositionFlagPath({
  basePairToAngle,
  index,
  layer,
  orf,
  radiusIn,
  sequenceLength,
  vMargin,
}: {
  basePairToAngle: (basePair: number) => number;
  index: number;
  layer: number;
  orf: ORF;
  radiusIn: d3.ScaleLinear<number, number>;
  sequenceLength: number;
  vMargin: number;
}): string {
  let start;
  let end;
  if (orf.strand() === 1) {
    start = orf.startPositions()[index];
    end = sequtil.mod(start + DISPLAY_START_POSITION_FLAG_THRESHOLD, sequenceLength);
  } else {
    end = (orf.startPositions()[index] + 1) % sequenceLength;
    start = sequtil.mod(end - DISPLAY_START_POSITION_FLAG_THRESHOLD, sequenceLength);
  }
  const {startAngle, endAngle} = getAngles(start, end, sequenceLength, basePairToAngle);
  const innerRadius = radiusIn(layer + 1);
  const outerRadius = radiusIn(layer) - vMargin;
  const arc = getArcForStrand(orf.strand());
  arc.innerRadius(innerRadius).outerRadius(outerRadius).startAngle(startAngle).endAngle(endAngle);
  // @ts-expect-error: Datum doesn't satisfy DefaultArcObject
  return arc(orf);
}
